/* eslint-disable @typescript-eslint/no-namespace */

export interface AddinCommand {
    type: 'openByCaseRefNum' | 'openByAppSerNum';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    args: any[];
}

export namespace Outlook {
    export type CategoryEnum = 'Matter' | 'Patent' | 'Trademark' | 'Reference';
    export const CategoryEnum = Object.freeze({
        UPLOADED_CATEGORY: 'Uploaded to First To File' as CategoryEnum,
        UPLOADED_CATEGORY_INCOMPLETE:  'Incomplete Upload to First To File' as CategoryEnum,
        UPLOADED_CATEGORY_FAILURE:  'Failed to upload to First to File' as CategoryEnum,
    });
}

export interface DialogMessageEvent {
    type: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any;
}

export interface RelatedFileGroup {
    id: string;
    display: string;
    files: FileWithProgress[];
}

/**
 * Transport data that corresponds with a FileWithProgress object
 * to be matched via the id property.
 */
export interface FileData {
    id: string;
    /**
     * base 64 encoded file data
     */
    data: string;
}

export interface FileWithProgress {
    id: string;
    name: string;
    title: string;
    dataFile?: File | boolean;
    type: string;
    size?: number;
    trifold: IP.TrifoldEnum;
    date: Date;
    subType?: number;
    urgent: boolean;
    docNotes: string;
    keywords: string;
    hasAttachments: boolean;
    isMainEmail?: boolean;
    // whether the file came from outlook api or manually provided
    entry: UploadEntryType;
    referenceType?: number;
    exportControl: boolean;
}

export interface CommonIPTrifold {
    identifier: string;
    ipType: IP.TypeEnum;
    L: Array<WsFile>;
    M: Array<WsFile>;
    R: Array<WsFile>;
}

export type UploadEntryType = 'outlook' | 'manual' | 'word';
export interface CommonIPStats {
    item: {
        ipType: IP.TypeEnum;
        identifier?: string;
    };
    successFiles: string[];
    failureFiles: string[];
}
export interface UploadStats {
    items: CommonIPStats[]
    emails: {
        [key: string]: {
            success: number;
            failure: number;
        }
    };
}

export interface CommonIPTrifoldUploadResponse extends BaseTrifoldUploadResponse {
    ipType: IP.TypeEnum;
    identifier: string;
}

export interface DisplayableFieldValue {
    key: string;
    value: string;
    field: string;
}
export interface DisplayableFieldResponse {
    displayable: DisplayableFieldValue[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    raw: any
}

export interface CommonIPDetail {
    references?: CaseReferenceDetails[];
    notes: CommonNote[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    biblio: any;
    workflow: WorkflowDO[];
}

export type CommonIPStateTabType = 'biblio' | 'trifold' | 'workflow' | 'notes' | 'references';
export type CommonIPStateViewType = 'trifold' | 'detail';
export interface CommonIPState {
    expanded: boolean;
    view?: CommonIPStateViewType;
    detailTabIndex: number;
    trifoldTabIndex: number;
    isDragOver: boolean;
}
export const DEFAULT_IP_STATE: CommonIPState = {
    expanded: true,
    view: undefined,
    detailTabIndex: -1,
    trifoldTabIndex: -1,
    isDragOver: false
}
export interface CommonIPLite {
    ipType: IP.TrifTypeEnum;
    identifier: string;
}
export interface CommonIP extends CommonIPLite {
    family: string;
    serial?: string;
    title: string;
    caseUrl?: string;
    exportControlStatus?: string;
    trifold?: CommonIPTrifold;

    // track state for ui (this is view state not case state)
    state?: CommonIPState;
    favorite?: boolean;
    // other keys
    detail?: CommonIPDetail;
}

export namespace WF {
    export type ActionEnum = 'ClaimCurrentTask' | 'UnclaimCurrentTask';
    export const ActionEnum = Object.freeze({
        ClaimCurrentTask: 'ClaimCurrentTask' as ActionEnum,
        UnclaimCurrentTask: 'UnclaimCurrentTask' as ActionEnum
    });
}
export namespace IP {
    export type TypeEnum = 'Matter' | 'Patent' | 'Trademark' | 'Reference' | 'CaseReference';
    export const TypeEnum = Object.freeze({
        Matter: 'Matter' as TypeEnum,
        Patent: 'Patent' as TypeEnum,
        Trademark: 'Trademark' as TypeEnum,
        Reference: 'Reference' as TypeEnum,
        CaseReference: 'CaseReference' as TypeEnum
    });

    // Define a type that excludes 'Reference'
    export type TrifTypeEnum = Exclude<keyof typeof IP.TypeEnum, 'Reference' | 'CaseReference'>;
    export const TrifTypeEnum = Object.freeze({
        Matter: 'Matter' as TrifTypeEnum,
        Patent: 'Patent' as TrifTypeEnum,
        Trademark: 'Trademark' as TrifTypeEnum,
    });

    export type TrifoldEnum = 'L' | 'M' | 'R';
    export const TrifoldEnum = Object.freeze({
        L: 'L' as TrifoldEnum,
        M: 'M' as TrifoldEnum,
        R: 'R' as TrifoldEnum
    });

    export const ReferenceTypes =  Object.freeze({
        1: "Non-Patent Reference",
        2: "Foreign Patent",
        3: "US Patent"
    });

}

export namespace Sorting {
    export type DirectionEnum = 'asc' | 'desc';
    export const DirectionEnum = Object.freeze({
        ASC: {
            value: 'asc' as DirectionEnum,
            label: 'Ascending'
        },
        DESC: {
            value: 'desc' as DirectionEnum, 
            label: 'Descending'
        }
    });
    
    export type SortByEnum = 'title' | 'document_date' | 'document_sub_type' | 'file_name';
    export const SortByEnum = Object.freeze({
        TITLE: {
            value: 'title' as SortByEnum,
            label: 'Title'
        },
        DOCUMENT_DATE: {
            value: 'document_date' as SortByEnum,
            label: 'Document Date'
        },
        DOCUMENT_SUB_TYPE: {
            value: 'document_sub_type' as SortByEnum,
            label: 'Document Sub-Type'
        },
        FILE_NAME: { 
            value: 'file_name' as SortByEnum,
            label: 'File Name'
        }
    });
}

export const DocumentActionsTypes = Object.freeze({
    1: "Details",
    2: "Preview",
    3: "Download",
});

export const PatentDocumentSubtypes: { [key: string]: string } = {};

export const TrademarkDocumentSubtypes: { [key: string]: string } = {};

export const MatterDocumentSubtypes: { [key: string]: string } = {};

export const DefaultIPTypesUploadBar = Object.freeze({
    1: "Reference Library",
});

export type UploadSubTypeGroupingMode = 'Doc Type' | 'Sub-Type';
export const UploadSubTypeGroupingMode = Object.freeze({
    DocType: "Doc Type" as UploadSubTypeGroupingMode,
    DocSubType: "Doc Sub-Type" as UploadSubTypeGroupingMode,
});

export const DST_PROP_BY_IP_TYPE : { [ key in IP.TrifTypeEnum ]: keyof DocumentSubType } = Object.freeze({
  Matter: 'usedInOm',
  Patent: 'usedInPatent',
  Trademark: 'usedInTm',
});
// represent subtypes nested below a document type
export type NestedSubType = { [key: string]: DocumentSubType[] };
// subtypes bucketized by IP Type
export type SubTypeByIpType = { [key in IP.TypeEnum]: NestedSubType };
export type ValueOf<T> = T[keyof T]
export type Entries<T> = [keyof T, ValueOf<T>][]
export type TrifoldPermissions = {
    access: string[];
    upload: string[];
    version: string[];
    lock: string[];
}
export type FieldMapWithDefault = {
    default: string;
    field: string;
    endpoint: string;
    // key in preferences whether it should be displayed
    displayPreference: string;
    // permission keys required to access and upload to this trifold
    permissions: TrifoldPermissions;
}
export type TrifoldFieldMap = {
    [key in IP.TrifoldEnum]: FieldMapWithDefault
}
export type TrifoldFieldMapByIPType = {
    [key in IP.TrifTypeEnum]: TrifoldFieldMap
};
export const TRIFOLD_FIELD_MAP: TrifoldFieldMapByIPType = Object.freeze({
    Patent: {
        L: {
            default: 'Correspondence',
            field: 'casetrifold.correspondence',
            endpoint: 'correspondence',
            displayPreference: 'FoldCor',
            permissions: {
                access: ['ROLE_CORRESPONDENCE_ACCESS'],
                upload: ['ROLE_CORRESPONDENCE_UPLOAD'],
                version: ['ROLE_CASE_MODIFY', 'ROLE_CORRESPONDENCE_VERSION'],
                lock: ['ROLE_CASE_MODIFY', 'ROLE_CORRESPONDENCE_LOCK']
            }
        },
        M: {
            default: 'File History',
            field: 'casetrifold.filehistory',
            endpoint: 'filing',
            displayPreference: 'FoldFil',
            permissions: {
                access: ['ROLE_PATENT_FILE_HISTORY_ACCESS'],
                upload: ['ROLE_FILE_HISTORY_UPLOAD'],
                version: ['ROLE_CASE_MODIFY', 'ROLE_FILE_HISTORY_VERSION'],
                lock: ['ROLE_CASE_MODIFY', 'ROLE_FILE_HISTORY_LOCK']
            }
        },
        R: {
            default: 'Documents',
            field: 'casetrifold.documents',
            endpoint: 'documents',
            displayPreference: 'FoldDoc',
            permissions: {
                access: ['ROLE_PATENT_DOCUMENTS_ACCESS'],
                upload: ['ROLE_DOCUMENTS_UPLOAD'],
                version: ['ROLE_CASE_MODIFY', 'ROLE_DOCUMENTS_VERSION'],
                lock: ['ROLE_CASE_MODIFY', 'ROLE_DOCUMENTS_LOCK']
            }
        },
    },
    Trademark: {
        L: {
            default: 'Client Corr.',
            field: 'trademarktrifold.clientcorr',
            endpoint: 'client',
            displayPreference: 'FoldCli',
            permissions: {
                access: ['ROLE_TRADEMARK_CLIENT_CORR_ACCESS'],
                upload: ['ROLE_TRADEMARK_CLIENT_UPLOAD'],
                version: ['ROLE_TRADEMARK_MODIFY', 'ROLE_TRADEMARK_CLIENT_VERSION'],
                lock: ['ROLE_TRADEMARK_MODIFY', 'ROLE_TRADEMARK_CLIENT_LOCK']
            }
        },
        M: {
            default: 'TM Office Corr.',
            field: 'trademarktrifold.tmofficecorr',
            endpoint: 'tm_office',
            displayPreference: 'FoldTmOf',
            permissions: {
                access: ['ROLE_TRADEMARK_OFFICE_CORR_ACCESS'],
                upload: ['ROLE_TRADEMARK_TM_OFFICE_UPLOAD'],
                version: ['ROLE_TRADEMARK_MODIFY', 'ROLE_TRADEMARK_TM_OFFICE_VERSION'],
                lock: ['ROLE_TRADEMARK_MODIFY', 'ROLE_TRADEMARK_TM_OFFICE_LOCK']
            }
        },
        R: {
            default: 'Opp Counsel Corr.',
            field: 'trademarktrifold.oppcounselcorr',
            endpoint: 'opposing_counsel',
            displayPreference: 'FoldCol',
            permissions: {
                access: ['ROLE_TRADEMARK_OPP_COUNSEL_CORR_ACCESS'],
                upload: ['ROLE_TRADEMARK_TM_OPPOSING_COUNSEL_UPLOAD'],
                version: ['ROLE_TRADEMARK_MODIFY', 'ROLE_TRADEMARK_TM_OPPOSING_COUNSEL_VERSION'],
                lock: ['ROLE_TRADEMARK_MODIFY', 'ROLE_TRADEMARK_TM_OPPOSING_COUNSEL_LOCK']
            }
        },
    },
    Matter: {
        L: {
            default: 'Correspondence',
            field: 'mattertrifold.correspondence',
            endpoint: 'correspondence',
            displayPreference: 'FoldMCor',
            permissions: {
                access: ['ROLE_MATTER_CORRESPONDENCE_ACCESS'],
                upload: ['ROLE_MATTER_CORR_UPLOAD'],
                version: ['ROLE_MATTER_MODIFY', 'ROLE_MATTER_CORR_VERSION'],
                lock: ['ROLE_MATTER_MODIFY', 'ROLE_MATTER_CORR_LOCK']
            }
        },
        M: {
            default: 'File History',
            field: 'mattertrifold.filehistory',
            endpoint: 'filing',
            displayPreference: 'FoldMFil',
            permissions: {
                access: ['ROLE_MATTER_FILE_HISTORY_ACCESS'],
                upload: ['ROLE_MATTER_FILE_HIS_UPLOAD'],
                version: ['ROLE_MATTER_MODIFY', 'ROLE_MATTER_FILE_HIS_VERSION'],
                lock: ['ROLE_MATTER_MODIFY', 'ROLE_MATTER_FILE_HIS_LOCK']
            }
        },
        R: {
            default: 'Documents',
            field: 'mattertrifold.documents',
            endpoint: 'documents',
            displayPreference: 'FoldMDoc',
            permissions: {
                access: ['ROLE_MATTER_DOCUMENT_ACCESS'],
                upload: ['ROLE_MATTER_DOCUMENTS_UPLOAD'],
                version: ['ROLE_MATTER_MODIFY', 'ROLE_MATTER_DOCUMENTS_VERSION'],
                lock: ['ROLE_MATTER_MODIFY', 'ROLE_MATTER_DOCUMENTS_LOCK']
            }
        },
    },
});
export type IpTypeLabel<T> = { key: T; value: string };
export const IP_TYPE_DEFAULTS : Array<IpTypeLabel<IP.TrifTypeEnum>> = [
    { key: IP.TrifTypeEnum.Patent, value: 'Patent' },
    { key: IP.TrifTypeEnum.Trademark, value: 'Trademark' },
    { key: IP.TrifTypeEnum.Matter, value: 'Other Matter' }
];

export type TrifoldOrderMap = { [key in IP.TrifTypeEnum]: IP.TrifoldEnum[] };
export const TRIFOLD_ORDER_DEFAULTS: TrifoldOrderMap = Object.freeze({
    Patent: ['L', 'M', 'R'],
    Trademark: ['L', 'M', 'R'],
    Matter: ['L', 'M', 'R'],
});

export type TrifoldDetailMap = { [key in IP.TrifTypeEnum]: { [key in IP.TrifoldEnum]: { label: string, access: boolean, upload: boolean, version: boolean, lock: boolean } } };
export const TRIFOLD_MAP_DEFAULTS: TrifoldDetailMap = Object.freeze({
    Patent: {
        L: { label: 'Correspondence', access: true, upload: false, version: false, lock: false },
        M: { label: 'File History', access: true, upload: false, version: false, lock: false },
        R: { label: 'Documents', access: true, upload: false, version: false, lock: false },
    },
    Trademark: {
        L: { label: 'Client Corr.', access: true, upload: false, version: false, lock: false },
        M: { label: 'TM Office Corr.', access: true, upload: false, version: false, lock: false },
        R: { label: 'Opp Counsel Corr.', access: true, upload: false, version: false, lock: false },
    },
    Matter: {
        L: { label: 'Correspondence', access: true, upload: false, version: false, lock: false },
        M: { label: 'File History', access: true, upload: false, version: false, lock: false },
        R: { label: 'Documents', access: true, upload: false, version: false, lock: false },
    },
});

export const TRIFOLD_FIELD_DEFAULTS: { [key in IP.TrifTypeEnum] : string; } = Object.freeze({
    Patent: 'Correspondence',
    Trademark: 'Client Correspondence',
    Matter: 'Correspondence',
});

export const IP_TYPE_ENDPOINT_MAP: { [key in IP.TypeEnum] : { primary: string; files: string; }} = {
    Matter: {
        primary: "matters",
        files: "matterfiles"
    },
    Patent: {
        primary: "cases",
        files: "casefiles"
    },
    Trademark: {
        primary: "trademarks",
        files: "trademarkfiles"
    },
    Reference: {
        primary: "reference",
        files: "reference"
    },
    CaseReference: {
        primary: "reference",
        files: "reference"
    }
};

export interface AccountGeneralSettings {
    createdByUserId?: number;
    displayName?: string;
    genericOne?: string;
    genericThree?: string;
    genericTwo?: string;
    isActive?: string;
    isCustomFieldName?: string;
    searchType?: string;
    settingId?: number;
    type?: string;
}

/**
 * Add File Event
 */
export interface AddFileEvent {
    caseNumber?: string;
    displayName?: string;
    eventId?: string;
    eventUrl?: string;
    fileId?: number;
}

/**
 * Agent Case Response
 */
export interface AgentCase {
    caseFamilyNm?: string;
    caseId?: number;
    country?: string;
    fileDate?: Date;
    primaryCaseNm?: string;
    serialNumber?: string;
    title?: string;
}

/**
 * The type of application type.
 */
export interface ApplicationType {
    id?: string;
    name?: string;
}

export interface CaseDetails {
    applicationType?: string;
    assignees?: string;
    attorneys?: string;
    caseFamilyNm?: string;
    caseId?: number;
    caseStatus?: string;
    caseSuffix?: string;
    caseType?: string;
    caseUrl?: string;
    country?: string;
    createByUser?: string;
    createDate?: Date;
    exportControlStatus?: string;
    externalRef?: string;
    fileDate?: string;
    genericField1?: string;
    genericField2?: string;
    genericField3?: string;
    genericField4?: string;
    genericField5?: string;
    genericField6?: string;
    groups?: string;
    inventors?: string;
    issueDate?: Date;
    jurisdiction?: string;
    modifiedDate?: Date;
    modifyByUser?: string;
    patentNumber?: string;
    primaryCaseNm?: string;
    priorityDate?: Date;
    priorityNumber?: string;
    products?: string;
    publicationDates?: Array<Date>;
    publicationNumber?: string;
    serialNumber?: string;
    technologies?: string;
    title?: string;
}

/**
 * Case Group
 */
export interface CaseGroup {
    caseNumber?: string;
    groupId?: number;
}

export interface CaseReferenceDetails extends ReferenceDetails {
    source?: number;
    sourceDate?: Date;
    sourceDisplay?: string;
    isSubmitted?: string;
    citationNotes?: string;
    citedDate?: Date;
    citationCategory?: string;
}

/**
 * Case Status
 */
export interface CaseStatus {
    id?: string;
    name?: string;
}



export interface BaseTrifoldUploadResponse {
    docSubTypeId?: number;
    docZonedDateTime?: Date;
    documentDate?: number;
    exportControlDoc?: string;
    fileId?: number;
    fileName?: string;
    filePriority?: string;
    fileSource?: string;
    hasAttachments?: string;
    receivedDate?: number;
    title?: string;
    trifold?: string;
    uploadUserName?: string;
}
export interface CaseUploadFile extends BaseTrifoldUploadResponse {
    primaryCaseNm?: string;
}

export interface TrademarkUploadFile extends BaseTrifoldUploadResponse {
    companyCaseNm?: string;
}

export interface MatterUploadFile extends BaseTrifoldUploadResponse {
    matterNm?: string;
}

/**
 * Country
 */
export interface Country {
    id?: string;
    name?: string;
}

/**
 * Delete File Note Response
 */
export interface DeleteFileNote {
    caseNumber?: string;
    fileId?: number;
    fileNoteId?: number;
}

/**
 * Delete Reference File Note
 */
export interface DeleteReferenceFileNote {
    fileId?: number;
    fileNoteId?: number;
}

/**
 * Displayable Value
 */
export interface DisplayableValueDO {
    display?: string;
    value?: string;
}

/**
 * Document Sub Type
 */
export interface DocumentSubType {
    agentTrifold?: string;
    doctypeId?: number;
    doctypeName?: string;
    id?: number;
    name?: string;
    omTrifold?: string;
    patentTrifold?: string;
    tmTrifold?: string;
    trifold?: string;
    usedInAgent?: boolean;
    usedInOm?: boolean;
    usedInOmScanning?: boolean;
    usedInPatent?: boolean;
    usedInPatentScanning?: boolean;
    usedInScanning?: boolean;
    usedInTm?: boolean;
    usedInTmScanning?: boolean;
    _group?: string;
}

/**
 * Document Type
 */
export interface DocumentType {
    agentTrifold?: string;
    id?: number;
    name?: string;
    usedInAgent?: boolean;
    usedInOm?: boolean;
    usedInPatent?: boolean;
    usedInTm?: boolean;
}

export interface Duration {
    nano?: number;
    negative?: boolean;
    seconds?: number;
    units?: Array<TemporalUnit>;
    zero?: boolean;
}

export interface EpsWsResponse<T> {
    code?: number;
    data?: T;
    exception?: unknown;
    graceLogins?: string;
    message?: string;
    type?: EpsWsResponse.TypeEnum;
}
export namespace EpsWsResponse {
    export type TypeEnum = 'SUCCESS' | 'GENERAL' | 'DUPLICATE_FILE' | 'DUPLICATE_REFERENCE' | 'INVALID_PARAMETER' | 'EMPTY_FILE' | 'CASE_DOESNT_EXIST' | 'TRADEMARK_DOESNT_EXIST' | 'MATTER_DOESNT_EXIST' | 'PERMISSION_DENIED' | 'EXPORT_CONTROL' | 'DUPLICATE_CASE' | 'DUPLICATE_EXTERNAL_REF' | 'ALREADY_EXISTS_IN_TRASHBIN' | 'FILE_IS_LOCKED' | 'CITATION_EXISTS' | 'SHARED_WITH_USER' | 'USED_BY_ACTIVE_WORKFLOW' | 'FILE_IS_CHECKED_OUT' | 'FILE_DOESNT_EXIST' | 'CAN_NOT_UPLOAD_FILES_TO_TRASHBIN' | 'FILE_NAME_MUST_MATCH' | 'FILE_EXT_MUST_MATCH' | 'FILE_EXTENSION_INACTIVE' | 'DUPLICATE_KEYWORD' | 'KEYWORD_DOES_NOT_EXIST' | 'FILE_NOTE_DOES_NOT_EXIST' | 'GROUP_DOES_NOT_EXIST' | 'DUPLICATE_GROUP' | 'FILE_NOT_CHECKED_OUT' | 'ALREADY_EXISTS_IN_TRIFOLD' | 'WORKFLOW_DOESNT_EXIST' | 'WORKFLOW_TASK_DOESNT_EXIST' | 'INVALID_RENUMBER_STATE' | 'CHANGE_NOT_ALLOWED';
    export const TypeEnum = {
        SUCCESS: 'SUCCESS' as TypeEnum,
        GENERAL: 'GENERAL' as TypeEnum,
        DUPLICATEFILE: 'DUPLICATE_FILE' as TypeEnum,
        DUPLICATEREFERENCE: 'DUPLICATE_REFERENCE' as TypeEnum,
        INVALIDPARAMETER: 'INVALID_PARAMETER' as TypeEnum,
        EMPTYFILE: 'EMPTY_FILE' as TypeEnum,
        CASEDOESNTEXIST: 'CASE_DOESNT_EXIST' as TypeEnum,
        TRADEMARKDOESNTEXIST: 'TRADEMARK_DOESNT_EXIST' as TypeEnum,
        MATTERDOESNTEXIST: 'MATTER_DOESNT_EXIST' as TypeEnum,
        PERMISSIONDENIED: 'PERMISSION_DENIED' as TypeEnum,
        EXPORTCONTROL: 'EXPORT_CONTROL' as TypeEnum,
        DUPLICATECASE: 'DUPLICATE_CASE' as TypeEnum,
        DUPLICATEEXTERNALREF: 'DUPLICATE_EXTERNAL_REF' as TypeEnum,
        ALREADYEXISTSINTRASHBIN: 'ALREADY_EXISTS_IN_TRASHBIN' as TypeEnum,
        FILEISLOCKED: 'FILE_IS_LOCKED' as TypeEnum,
        CITATIONEXISTS: 'CITATION_EXISTS' as TypeEnum,
        SHAREDWITHUSER: 'SHARED_WITH_USER' as TypeEnum,
        USEDBYACTIVEWORKFLOW: 'USED_BY_ACTIVE_WORKFLOW' as TypeEnum,
        FILEISCHECKEDOUT: 'FILE_IS_CHECKED_OUT' as TypeEnum,
        FILEDOESNTEXIST: 'FILE_DOESNT_EXIST' as TypeEnum,
        CANNOTUPLOADFILESTOTRASHBIN: 'CAN_NOT_UPLOAD_FILES_TO_TRASHBIN' as TypeEnum,
        FILENAMEMUSTMATCH: 'FILE_NAME_MUST_MATCH' as TypeEnum,
        FILEEXTMUSTMATCH: 'FILE_EXT_MUST_MATCH' as TypeEnum,
        FILEEXTENSIONINACTIVE: 'FILE_EXTENSION_INACTIVE' as TypeEnum,
        DUPLICATEKEYWORD: 'DUPLICATE_KEYWORD' as TypeEnum,
        KEYWORDDOESNOTEXIST: 'KEYWORD_DOES_NOT_EXIST' as TypeEnum,
        FILENOTEDOESNOTEXIST: 'FILE_NOTE_DOES_NOT_EXIST' as TypeEnum,
        GROUPDOESNOTEXIST: 'GROUP_DOES_NOT_EXIST' as TypeEnum,
        DUPLICATEGROUP: 'DUPLICATE_GROUP' as TypeEnum,
        FILENOTCHECKEDOUT: 'FILE_NOT_CHECKED_OUT' as TypeEnum,
        ALREADYEXISTSINTRIFOLD: 'ALREADY_EXISTS_IN_TRIFOLD' as TypeEnum,
        WORKFLOWDOESNTEXIST: 'WORKFLOW_DOESNT_EXIST' as TypeEnum,
        WORKFLOWTASKDOESNTEXIST: 'WORKFLOW_TASK_DOESNT_EXIST' as TypeEnum,
        INVALIDRENUMBERSTATE: 'INVALID_RENUMBER_STATE' as TypeEnum,
        CHANGENOTALLOWED: 'CHANGE_NOT_ALLOWED' as TypeEnum
    };
}

export interface EpsWsWrapper<T> {
    epsws?: EpsWsResponse<T>;
}

export interface FileDocketEventsId {
    eventId?: string;
    fileId?: number;
}

export interface FileDocketEvents {
    createDate?: Date;
    createdByUserId?: number;
    displayName?: string;
    eventUrl?: string;
    id?: FileDocketEventsId;
    modifiedByUserId?: number;
    modifiedDate?: Date;
}

/**
 * File Event Response
 */
export interface FileEvent {
    caseNumber?: string;
    eventId?: string;
    fileId?: number;
}

/**
 * File Keyword Response
 */
export interface FileKeyword {
    caseNumber?: string;
    fileId?: number;
    keywordText?: string;
}

/**
 * File Note Response
 */
export interface FileNote {
    caseNumber?: string;
    fileId?: number;
    fileNote?: string;
}

/**
 * File Priority
 */
export interface FilePriority {
    id?: string;
    name?: string;
}

/**
 * File Reference
 */
export interface FileReference {
    documentDate?: Date;
    fileId?: number;
    fileName?: string;
    fullFileName?: string;
    title?: string;
    type?: string;
}

/**
 * FileVersion
 */
export interface FileVersionDO {
    versionNumber?: number;
    createDate?: number;
    createdBy?: string;
    fileId?: number;
}

export interface GridRequest {
    quickSearch?: string;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    currentIndex: number;
    displayNumber: number;
}

export interface GridCriteria<T> {
    active?: string;
    administrator?: boolean;
    currentPage?: number;
    displayNumber: number;
    endIndex?: number;
    maxRow?: number;
    minRow?: number;
    results: Array<T>;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    startIndex: number;
    totalPages?: number;
    totalRows: number;
    utc?: string;
}

export interface GroupsDTO {
    description?: string;
    groupId?: number;
    title?: string;
}

/**
 * Image Response
 */
export interface ImageResponse {
    /**
     * The Base64 Encoded Image
     */
    data?: string;
    /**
     * The Content Type
     */
    type?: string;
}

/**
 * Jurisdiction
 */
export interface Jurisdiction {
    id?: number;
    name?: string;
}

/**
 * Maintenance Status
 */
export interface MaintenanceStatus {
    id?: string;
    name?: string;
}

/**
 * Mark Type
 */
export interface MarkType {
    id?: string;
    name?: string;
}

export interface MatterDetails {
    attorneys?: string;
    caseUrl?: string;
    catGenericField1?: string;
    clientName?: string;
    clientReference?: string;
    country?: string;
    createByUser?: string;
    createDate?: Date;
    expireDate?: Date;
    exportControlStatus?: string;
    externalRef?: string;
    filingDate?: string;
    genericDate1?: Date;
    genericDate2?: Date;
    genericDate3?: Date;
    genericDate4?: Date;
    genericDate5?: Date;
    genericDate6?: Date;
    genericField1?: string;
    genericField2?: string;
    genericField3?: string;
    genericField4?: string;
    genericField5?: string;
    genericField6?: string;
    groups?: string;
    matterFamilyNm?: string;
    matterId?: number;
    matterNm?: string;
    matterSuffix?: string;
    matterTitle?: string;
    matterType?: string;
    modifiedByUser?: string;
    modifiedDate?: Date;
    outsideAttorneys?: string;
    outsideCounselDocketNm?: string;
    ownerName?: string;
    priorityCountry?: string;
    priorityDate?: Date;
    priorityNumber?: string;
    products?: string;
    registrationDate?: Date;
    registrationNumber?: string;
    serialNumber?: string;
    status?: string;
    statusDate?: Date;
    technologies?: string;
}

export interface MatterDO {
    country?: string;
    exportControlStatus?: string;
    fileDate?: Date;
    matterFamilyNm?: string;
    matterId?: number;
    matterNm?: string;
    matterTitle?: string;
    serialNumber?: string;
}

/**
 * Matter Case Link Response
 */
export interface MatterLink {
    caseUrl?: string;
    matterNm?: string;
}

/**
 * Matter Status
 */
export interface MatterStatus {
    id?: string;
    name?: string;
}

export interface MatterTrifoldData {
    correspondence?: Array<WsFile>;
    documents?: Array<WsFile>;
    filing?: Array<WsFile>;
    matterNm?: string;
}

/**
 * Matter
 */
export interface Matter {
    matterId?: number;
    matterNm?: string;
}

/**
 * Matter Type
 */
export interface MatterType {
    id?: string;
    name?: string;
}


/**
 * Matter Version Upload Response
 */
export interface MatterUpload {
    fileId?: number;
    matterNm?: string;
    uploadUserName?: string;
}

/**
 * Patent Case Type
 */
export interface PatentCaseType {
    id?: number;
    name?: string;
}

/**
 * Patent
 */
export interface PatentDO {
    caseFamilyNm?: string;
    caseId?: number;
    country?: string;
    exportControlStatus?: string;
    fileDate?: Date;
    primaryCaseNm?: string;
    serialNumber?: string;
    title?: string;
}

/**
 * Patent Case Link Response
 */
export interface PatentLink {
    caseUrl?: string;
    primaryCaseNm?: string;
}

export interface PatentTrifoldData {
    correspondence?: Array<WsFile>;
    documents?: Array<WsFile>;
    filing?: Array<WsFile>;
    primaryCaseNm?: string;
}

/**
 * Patent
 */
export interface Patent {
    caseId?: number;
    primaryCaseNm?: string;
}

/**
 * Patent Version Upload Response
 */
export interface PatentUpload {
    fileId?: number;
    primaryCaseNm?: string;
    uploadUserName?: string;
}

/**
 * Publication
 */
export interface Publication {
    countryCode?: string;
    kindCode?: string;
    name?: string;
}

export interface ReferenceDetails {
    applicant?: string;
    applicantNumber?: string;
    articleDate?: string;
    articleTitle?: string;
    assignee?: string;
    author?: string;
    city?: string;
    countryCode?: string;
    createDate?: Date;
    createdByUser?: string;
    customCitation?: string;
    fileAvailable?: string;
    fileId?: number;
    fileName?: string;
    fileNameDisplay?: string;
    fileSize?: number;
    fileSource?: string;
    inventors?: string;
    issueDate?: Date;
    itemTitle?: string;
    kindCode?: string;
    location?: string;
    modifiedByUser?: string;
    modifiedDate?: Date;
    pages?: string;
    patentNumber?: string;
    publicationDate?: Date;
    publicationNumber?: string;
    publisher?: string;
    referenceType?: number;
    referenceTypeDisplay?: string;
    requirementsOverride?: boolean;
    titleDisplay?: string;
    useCustomCitation?: boolean;
    volumeNumber?: string;
}

/**
 * Reference File Keyword
 */
export interface ReferenceFileKeyword {
    fileId?: number;
    keywordText?: string;
}

/**
 * Reference File Note
 */
export interface ReferenceFileNote {
    fileId?: number;
    fileNote?: string;
}

/**
 * Reference Source
 */
export interface ReferenceSource {
    id?: number;
    name?: string;
}

/**
 * Reference Type
 */
export interface ReferenceType {
    id?: string;
    name?: string;
}

/**
 * Reference Version Upload Response
 */
export interface ReferenceUploadDO {
    fileId?: number;
    uploadUserName?: string;
}

export interface RefUploadFile {
    assocType?: RefUploadFile.AssocTypeEnum;
    assocValue?: string;
    assocValues?: Array<string>;
    docZonedDateTime?: Date;
    documentDate?: number;
    exportControlDoc?: string;
    fileId?: number;
    fileName?: string;
    fileSource?: string;
    receivedDate?: number;
    referenceType?: string;
    sourceDate?: Date;
    sourceId?: number;
    title?: string;
    uploadUserName?: string;
}
export namespace RefUploadFile {
    export type AssocTypeEnum = 'primaryCaseNm';
    export const AssocTypeEnum = {
        PrimaryCaseNm: 'primaryCaseNm' as AssocTypeEnum
    };
}

export interface RefFileResponse extends BaseTrifoldUploadResponse {
    uploadUserName?: string;
    fileName: string;
}

/**
 * Simple User
 */
export interface SimpleUserDO {
    firstName?: string;
    id?: number;
    lastName?: string;
}

/**
 * Trademark State Registration
 */
export interface StateRegistration {
    /**
     * Two Character State Code
     */
    id?: string;
    /**
     * Full name of state
     */
    name?: string;
}

export interface TemporalUnit {
    dateBased?: boolean;
    duration?: Duration;
    durationEstimated?: boolean;
    timeBased?: boolean;
}

/**
 * Trademark Application Type
 */
export interface TrademarkApplicationType {
    id?: string;
    name?: string;
}

/**
 * Trademark Case Type
 */
export interface TrademarkCaseType {
    id?: number;
    name?: string;
}

export interface TrademarkDetails {
    assignee?: string;
    attorney?: string;
    caseFamily?: string;
    caseSuffix?: string;
    caseType?: string;
    caseUrl?: string;
    catGenericField1?: string;
    clientName?: string;
    clientReference?: string;
    companyCaseNm?: string;
    country?: string;
    createByUser?: string;
    createDate?: Date;
    depictionMark?: string;
    displayName?: string;
    expireDate?: Date;
    exportControlStatus?: string;
    externalRef?: string;
    filingBasis?: string;
    filingDate?: string;
    filingJurisdicton?: string;
    firstUseCommerce?: Date;
    firstUseDate?: Date;
    genericField1?: string;
    genericField2?: string;
    genericField3?: string;
    genericField4?: string;
    genericField5?: string;
    genericField6?: string;
    goodAndServicesClasses?: string;
    group?: string;
    lastRenewalDate?: Date;
    markDrawingCode?: string;
    modifiedDate?: Date;
    modifyByUser?: string;
    nextRenewalDate?: Date;
    outsideAttorney?: string;
    ownerName?: string;
    priorityCountry?: string;
    priorityDate?: Date;
    priorityNumber?: string;
    proOutsideDocketNm?: string;
    products?: string;
    regType?: string;
    registrationDate?: Date;
    registrationNumber?: string;
    serialNumber?: string;
    standardCharacterClaim?: string;
    state?: string;
    status?: string;
    statusDate?: Date;
    subStatus?: string;
    subStatusDate?: Date;
    technologies?: string;
    trademarkId?: number;
    trademarkNm?: string;
}

export interface TrademarkDO {
    caseFamilyNm?: string;
    companyCaseNm?: string;
    country?: string;
    exportControlStatus?: string;
    fileDate?: Date;
    serialNumber?: string;
    trademarkId?: number;
    trademarkNm?: string;
}

/**
 * Trademark Drawing Code
 */
export interface TrademarkDrawingCode {
    id?: string;
    name?: string;
}

/**
 * Trademark Filing Basis
 */
export interface TrademarkFilingBasis {
    id?: string;
    name?: string;
}

/**
 * Trademark Filing Jurisdiction
 */
export interface TrademarkFilingJurisdiction {
    id?: string;
    name?: string;
}

/**
 * File Trademark Image Upload Response
 */
export interface TrademarkImageUpload {
    companyCaseNm?: string;
    imageFileName?: string;
    uploadUserName?: string;
}

/**
 * Trademark Case Link Response
 */
export interface TrademarkLink {
    caseUrl?: string;
    companyCaseNm?: string;
}

/**
 * Trademark Status
 */
export interface TrademarkStatus {
    id?: string;
    name?: string;
}

export interface TrademarkTrifoldData {
    client?: Array<WsFile>;
    companyCaseNm?: string;
    oppCounsel?: Array<WsFile>;
    tmOffice?: Array<WsFile>;
}

/**
 * Trademark
 */
export interface Trademark {
    companyCaseNm?: string;
    trademarkId?: number;
}

/**
 * Trademark Version Upload Response
 */
export interface TrademarkUpload {
    companyCaseNm?: string;
    fileId?: number;
    uploadUserName?: string;
}

/**
 * File Check-in Response
 */
export interface UploadCheckin {
    caseNumber?: string;
    fileId?: number;
    uploadUserName?: string;
}

/**
 * Version
 */
export interface Version {
    version?: string;
}

/**
 * Workflow Definition Template
 */
export interface WorkflowDefinitionDO {
    id?: number;
    name?: string;
    state?: DisplayableValueDO;
    type?: WorkflowDefinitionDO.TypeEnum;
}
export namespace WorkflowDefinitionDO {
    export type TypeEnum = 'Matter' | 'Patent' | 'Trademark';
    export const TypeEnum = {
        Matter: 'Matter' as TypeEnum,
        Patent: 'Patent' as TypeEnum,
        Trademark: 'Trademark' as TypeEnum
    };
}

/**
 * Workflow
 */
export interface WorkflowDO {
    caseNumber?: string;
    completeDate?: Date;
    createDate?: Date;
    currentTask?: WorkflowTaskDO;
    definition?: WorkflowDefinitionDO;
    dueDate?: Date;
    id?: number;
    instructions?: string;
    modifiedDate?: Date;
    state?: DisplayableValueDO;
    urgent?: boolean;
}

/**
 * Workflow file
 */
export interface WorkflowFileDO {
    fileId: number;
    type: IP.TypeEnum;
}

/**
 * Workflow Initiation
 */
export interface WorkflowInitiateDO {
    dueDate?: Date;
    files?: Array<number>;
    instructions?: string;
    urgencyInterval?: number;
    urgencyType?: WorkflowInitiateDO.UrgencyTypeEnum;
    workflowDefId?: number;
}
export namespace WorkflowInitiateDO {
    export type UrgencyTypeEnum = 'None' | 'Always' | 'DaysFromDueDate';
    export const UrgencyTypeEnum = {
        None: 'None' as UrgencyTypeEnum,
        Always: 'Always' as UrgencyTypeEnum,
        DaysFromDueDate: 'DaysFromDueDate' as UrgencyTypeEnum
    };
}


/**
 * Workflow Task Checklist Item Definition
 */
export interface WorkflowTaskChecklistItemDefinitionDO {
    name?: string;
    order?: number;
}

/**
 * Workflow Task Checklist Item
 */
export interface WorkflowTaskChecklistItemDO {
    name?: string;
    order?: number;
    status?: WorkflowTaskChecklistItemDO.StatusEnum;
}
export namespace WorkflowTaskChecklistItemDO {
    export type StatusEnum = 'Complete' | 'NA' | 'Incomplete';
    export const StatusEnum = {
        Complete: 'Complete' as StatusEnum,
        NA: 'NA' as StatusEnum,
        Incomplete: 'Incomplete' as StatusEnum
    };
}

/**
 * Workflow Task Comment
 */
export interface WorkflowTaskCommentDO {
    comment?: string;
    createDate?: Date;
}

/**
 * Workflow Task Definition Template
 */
export interface WorkflowTaskDefinitionDO {
    checklist?: Array<WorkflowTaskChecklistItemDefinitionDO>;
    checklistRequired?: boolean;
    id?: number;
    name?: string;
    step?: number;
    workflowDefId?: number;
    workflowName?: string;
    workflowType?: WorkflowTaskDefinitionDO.WorkflowTypeEnum;
}
export namespace WorkflowTaskDefinitionDO {
    export type WorkflowTypeEnum = 'Matter' | 'Patent' | 'Trademark';
    export const WorkflowTypeEnum = {
        Matter: 'Matter' as WorkflowTypeEnum,
        Patent: 'Patent' as WorkflowTypeEnum,
        Trademark: 'Trademark' as WorkflowTypeEnum
    };
}

/**
 * Workflow Task
 */
export interface WorkflowTaskDO {
    assignedTo?: SimpleUserDO;
    completeDate?: Date;
    createDate?: Date;
    definition?: WorkflowTaskDefinitionDO;
    dueDate?: Date;
    id?: number;
    modifiedDate?: Date;
    priority?: WorkflowTaskDO.PriorityEnum;
    rejectedInPrevStep?: boolean;
    reviewLoop?: boolean;
    state?: DisplayableValueDO;
    workflowId?: number;
}
export namespace WorkflowTaskDO {
    export type PriorityEnum = 'Medium' | 'High' | 'Low';
    export const PriorityEnum = {
        Medium: 'Medium' as PriorityEnum,
        High: 'High' as PriorityEnum,
        Low: 'Low' as PriorityEnum
    };
}

export interface WorkflowUserTaskCountsDO {
    groupUnclaimed?: number;
    userClaimed?: number;
    userUnclaimed?: number;
}

export interface WsAccountDetails {
    accountDateFormat?: string;
    apiNotice?: string;
    apiVersion?: string;
    customFields?: { [key: string]: string; };
    displayAssignee?: boolean;
    displayMatterTrifoldSubtype?: boolean;
    displayMtrDocEventId?: string;
    displayNationalFilingDate?: boolean;
    displayPatCaseLink?: string;
    displayPatDocEventId?: string;
    displayPatTrifoldSubtype?: boolean;
    displayTmDocEventId?: string;
    displayTmTrifoldSubtype?: boolean;
    emailAddress?: string;
    exportControlUserDefault?: string;
    firstName?: string;
    generalSettings?: Array<AccountGeneralSettings>;
    lastName?: string;
    matterExportControlDocDefault?: string;
    matterExportControlStatusDefault?: string;
    matterTrifoldOrder?: string;
    overrideMatterTrifoldSubtype?: boolean;
    overridePatTrifoldSubtype?: boolean;
    overrideTmTrifoldSubtype?: boolean;
    patentExportControlDocDefault?: string;
    patentExportControlStatusDefault?: string;
    patentTrifoldOrder?: string;
    preferences?: { [key: string]: string; };
    roles?: Array<string>;
    tmExportControlDocDefault?: string;
    tmExportControlStatusDefault?: string;
    tmTrifoldOrder?: string;
    userName?: string;
    clientName?: string;
    userSubjectToExportControl?: string;
}

export interface WsEvent {
    createDate?: number;
    eventType?: string;
    fileId?: number;
    isSubmitted?: string;
    primaryCaseNm?: string;
    referenceName?: string;
    referenceSourceDate?: Date;
    referenceSourceId?: number;
}

export interface WsFile {
    assignDate?: string;
    completeDate?: string;
    currentDueDate?: string;
    docSubtype?: string;
    docSubtypeId?: number;
    docType?: string;
    docTypeId?: number;
    documentDate?: string;
    date?: Date;
    documentDateTime?: string;
    fileId?: number;
    fileName?: string;
    filePriority?: string;
    fileSize?: number;
    fileSource?: string;
    fullFileName?: string;
    genericField1?: string;
    genericField2?: string;
    genericField3?: string;
    genericField4?: string;
    hasAttachments?: string;
    receivedDate?: string;
    receivedDateEpochTime?: number;
    status?: string;
    technicalDocument?: string;
    title?: string;
    usersByAssignBy?: string;
    usersByAssignTo?: string;
    versions?: Array<FileVersionDO>;
}

export interface WsFileWithData extends WsFile {
    data?: Blob;
}

export interface WsKeyword {
    keyword?: string;
}

export interface CommonNote {
    createByUser?: string;
    createDate?: number;
    notes?: string;
}

export interface FileNotesDTO {
    createDate?: number;
    createdByUser?: string;
    fileNoteId?: number;
    notes?: string;
}

/**
 * Workflow note
 */
export interface WorkflowNoteDO {
    createDate?: number;
    note?: string;
}
